import React from 'react'
import { F0TagPerson, F0TagStatus, F0TagTeam, F0Text } from '@factorialco/f0-react'
import { RichTextDisplay, Select, Stack } from '@factorialco/f0-react/dist/experimental'
import i18n from 'modules/core/lib/i18n'
import TicketPriority from 'modules/ticketing/components/TicketField/TicketPriority'
import TicketStatus from 'modules/ticketing/components/TicketField/TicketStatus'
import { AssigneeSelect } from 'modules/ticketing/components/AssigneeSelect/AssigneeSelect'
import { useTeams } from 'modules/ticketing/hooks/useTeams'
import type { TicketDetailQuery } from 'generated/resources/graphql'
import { formatDate, formatTimeRemaining, isOverdue } from 'modules/ticketing/lib/ticketTime'
import { getTicketStatusLabel, getTicketStatusOptions } from 'modules/ticketing/lib/ticketStatus'
import {
  getTicketPriorityLabel,
  getTicketPriorityOptions,
} from 'modules/ticketing/lib/ticketPriority'

export type TicketDetailListProps = {
  ticket: TicketDetailQuery['ticketing']['ticket']
  onStatusChange?: (newStatus: string) => void
  onPriorityChange?: (newPriority: string) => void
  onAssigneeChange?: (fields: { assigneeTeamId?: string; assigneeMemberId?: string | null }) => void
}

export const TicketDetailList: React.FC<TicketDetailListProps> = ({
  ticket,
  onStatusChange,
  onPriorityChange,
  onAssigneeChange,
}) => {
  const { teams } = useTeams()

  if (!ticket) return null

  const requesterDisplayName = ticket.requesterName ?? `#${ticket.requesterId}`

  return (
    <Stack className="grid grid-cols-2 gap-6 pt-4">
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.status')} />
        {ticket.canEdit && onStatusChange ? (
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.status')}
            value={ticket.status}
            onChange={(value: string) => onStatusChange(value)}
            options={getTicketStatusOptions().map((s) => ({
              value: s,
              label: getTicketStatusLabel(s),
            }))}
            hideLabel
          />
        ) : (
          <TicketStatus status={ticket.status} />
        )}
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.priority')} />
        {ticket.canEdit && onPriorityChange ? (
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.priority')}
            value={ticket.priority}
            onChange={(value: string) => onPriorityChange(value)}
            options={getTicketPriorityOptions().map((p) => ({
              value: p,
              label: getTicketPriorityLabel(p),
            }))}
            hideLabel
          />
        ) : (
          <TicketPriority priority={ticket.priority} />
        )}
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.assignee_team')} />
        {ticket.canEdit && onAssigneeChange ? (
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.assignee_team')}
            value={ticket.assigneeTeamId != null ? String(ticket.assigneeTeamId) : ''}
            onChange={(value: string) => {
              onAssigneeChange({ assigneeTeamId: value, assigneeMemberId: null })
            }}
            options={teams.map((t) => ({
              value: String(t.value),
              label: t.label,
            }))}
            hideLabel
          />
        ) : ticket.assigneeTeamName ? (
          <F0TagTeam name={ticket.assigneeTeamName} src={ticket.assigneeTeamName} />
        ) : (
          <F0TagStatus
            text={i18n.t('ticketing.inbox.assignee_team_unassigned')}
            variant="neutral"
          />
        )}
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.assignee_member')} />
        {ticket.canEdit && onAssigneeChange ? (
          <AssigneeSelect
            label={''}
            defaultValue={ticket.assigneeMemberId != null ? String(ticket.assigneeMemberId) : ''}
            teamId={ticket.assigneeTeamId ?? undefined}
            onChange={(item) => {
              onAssigneeChange({
                assigneeTeamId: ticket.assigneeTeamId ?? undefined,
                assigneeMemberId:
                  item === null || !item.subItems?.[0]?.subId
                    ? null
                    : String(item.subItems[0].subId),
              })
            }}
            disabled={ticket.assigneeTeamId == null || ticket.assigneeTeamId === '0'}
          />
        ) : ticket.assigneeMemberName ? (
          <F0TagPerson name={ticket.assigneeMemberName} />
        ) : (
          <F0TagStatus
            text={i18n.t('ticketing.inbox.assignee_member_unassigned')}
            variant="neutral"
          />
        )}
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.requested_by')} />
        <F0TagPerson name={requesterDisplayName} />
      </Stack>
      <Stack gap="2">
        <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.created_at')} />
        <F0Text content={formatDate(ticket.createdAt)} />
      </Stack>
      {ticket.slaDueAt && (
        <Stack gap="2">
          <F0Text variant="label" content={i18n.t('ticketing.inbox.columns.time_remaining')} />
          {isOverdue(ticket.timeRemainingToSlaSeconds) ? (
            <F0TagStatus
              text={formatTimeRemaining(ticket.timeRemainingToSlaSeconds)}
              variant="critical"
            />
          ) : (
            <F0Text content={formatTimeRemaining(ticket.timeRemainingToSlaSeconds)} />
          )}
        </Stack>
      )}
      {ticket.description && (
        <Stack className="col-span-2" gap="2">
          <F0Text variant="label" content={i18n.t('ticketing.inbox.one_ticket_card.description')} />
          <RichTextDisplay format="markdown" content={ticket.description} />
        </Stack>
      )}
    </Stack>
  )
}
